import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X } from "lucide-react";
import CategoryBadge from "../components/CategoryBadge";
import { slugify, projectPath } from "../lib/seo";

const EMPTY = {
  title: "",
  description: "",
  category: "Transport",
  county: "București",
  city: "",
  authority: "",
  slug: "",
  status: "propus",
};

export default function Admin() {
  const { user, loading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [meta, setMeta] = useState({ categories: [], counties: [] });
  const [editing, setEditing] = useState(null); // null | "new" | project_id
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/meta").then(({ data }) => setMeta(data)); }, []);
  const load = () => api.get("/projects", { params: { sort: "new" } }).then(({ data }) => setProjects(data));
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (loading) return <div className="container-civic py-16">Se încarcă…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const startNew = () => { setForm(EMPTY); setEditing("new"); };
  const startEdit = (p) => {
    setForm({
      title: p.title, description: p.description, category: p.category,
      county: p.county, city: p.city, authority: p.authority, slug: p.slug || "",
      status: p.status,
    });
    setEditing(p.project_id);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: form.slug?.trim() || undefined,
      };
      if (editing === "new") {
        await api.post("/projects", payload);
        toast.success("Proiect adăugat");
      } else {
        await api.put(`/projects/${editing}`, payload);
        toast.success("Proiect actualizat");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Ștergi „${p.title}"?`)) return;
    try {
      await api.delete(`/projects/${p.project_id}`);
      toast.success("Proiect șters");
      load();
    } catch {
      toast.error("Nu s-a putut șterge");
    }
  };

  return (
    <main className="container-civic py-12 md:py-16">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="label-overline">Administrare</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight mt-3">Proiecte</h1>
        </div>
        <button onClick={startNew} className="btn-primary" data-testid="admin-new-btn">
          <Plus size={16}/> Adaugă proiect
        </button>
      </div>

      {editing && (
        <form onSubmit={save} className="card-flat p-6 mt-8 grid md:grid-cols-2 gap-4" data-testid="admin-form">
          <div className="md:col-span-2 flex items-center justify-between">
            <h2 className="font-display text-xl font-bold">
              {editing === "new" ? "Proiect nou" : "Editează proiect"}
            </h2>
            <button type="button" onClick={() => setEditing(null)} className="text-foreground/60 hover:text-foreground">
              <X size={18} />
            </button>
          </div>
          <label className="md:col-span-2 text-sm">
            <span className="label-overline block mb-1">Titlu</span>
            <input required minLength={4} value={form.title} onChange={(e) => setForm((prev) => ({
              ...prev,
              title: e.target.value,
              slug: prev.slug ? prev.slug : slugify(e.target.value),
            }))}
              className="w-full border border-border rounded-sm px-3 py-2 bg-white" data-testid="admin-title-input"/>
          </label>
          <label className="md:col-span-2 text-sm">
            <span className="label-overline block mb-1">URL slug</span>
            <input
              value={form.slug}
              onChange={(e) => setForm({...form, slug: e.target.value})}
              className="w-full border border-border rounded-sm px-3 py-2 bg-white"
              placeholder="digitalizare-100-certificate-de-urbanism"
              data-testid="admin-slug-input"
            />
            <p className="mt-2 text-xs text-foreground/60">
              URL canonic: <span className="font-semibold text-foreground">{projectPath(form.category, form.slug || form.title)}</span>
            </p>
          </label>
          <label className="md:col-span-2 text-sm">
            <span className="label-overline block mb-1">Descriere</span>
            <textarea required minLength={10} rows={5} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
              className="w-full border border-border rounded-sm px-3 py-2 bg-white" data-testid="admin-description-input"/>
          </label>
          <label className="text-sm">
            <span className="label-overline block mb-1">Categorie</span>
            <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}
              className="w-full border border-border rounded-sm px-3 py-2 bg-white" data-testid="admin-category-select">
              {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <span className="label-overline block mb-1">Județ</span>
            <select value={form.county} onChange={(e) => setForm({...form, county: e.target.value})}
              className="w-full border border-border rounded-sm px-3 py-2 bg-white" data-testid="admin-county-select">
              {meta.counties.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <span className="label-overline block mb-1">Oraș</span>
            <input required value={form.city} onChange={(e) => setForm({...form, city: e.target.value})}
              className="w-full border border-border rounded-sm px-3 py-2 bg-white" data-testid="admin-city-input"/>
          </label>
          <label className="text-sm">
            <span className="label-overline block mb-1">Autoritate</span>
            <input required value={form.authority} onChange={(e) => setForm({...form, authority: e.target.value})}
              className="w-full border border-border rounded-sm px-3 py-2 bg-white" data-testid="admin-authority-input"/>
          </label>
          <label className="md:col-span-2 text-sm">
            <span className="label-overline block mb-1">Stare</span>
            <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}
              className="w-full border border-border rounded-sm px-3 py-2 bg-white" data-testid="admin-status-select">
              {["propus", "în analiză", "aprobat", "respins"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <div className="md:col-span-2 flex gap-3 justify-end">
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>Renunță</button>
            <button type="submit" className="btn-primary" disabled={saving} data-testid="admin-save-btn">
              {saving ? "Salvez…" : "Salvează"}
            </button>
          </div>
        </form>
      )}

      <div className="mt-10 border-t border-l border-border" data-testid="admin-projects-list">
        {projects.map((p) => (
          <div key={p.project_id} className="grid md:grid-cols-12 gap-4 border-r border-b border-border p-5 bg-white items-center">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 flex-wrap">
                <CategoryBadge category={p.category} />
                <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-foreground/60">{p.status}</span>
              </div>
              <h3 className="font-display text-lg font-bold mt-2">{p.title}</h3>
              <p className="text-xs text-foreground/60 mt-1">{p.city}, {p.county} · {p.authority}</p>
            </div>
            <div className="md:col-span-3 text-sm tabular-nums">
              Scor <span className="font-bold">{p.score}</span> · {p.upvotes}↑ {p.downvotes}↓
            </div>
            <div className="md:col-span-2 flex gap-2 justify-end">
              <button className="btn-secondary !px-3 !py-2" onClick={() => startEdit(p)} data-testid={`admin-edit-${p.project_id}`}>
                <Edit3 size={14}/>
              </button>
              <button className="btn-secondary !px-3 !py-2 !text-destructive !border-destructive/40" onClick={() => remove(p)} data-testid={`admin-delete-${p.project_id}`}>
                <Trash2 size={14}/>
              </button>
            </div>
          </div>
        ))}
        {projects.length === 0 && (
          <div className="p-12 text-center text-foreground/60 border-r border-b border-border bg-white">
            Niciun proiect deocamdată.
          </div>
        )}
      </div>
    </main>
  );
}
